import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Avatar from "@mui/material/Avatar";
import Link from "@mui/material/Link";
import {
  Alert,
  Collapse,
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import "./WebsiteActionTable.css";
import { Box } from "@mui/system";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import WebsiteAdditionalInfo from "./WebsiteAdditionalInfo/WebsiteAdditionalInfo";
import { selectUserData } from "../../../../../../features/userSlice";
import { useSelector } from "react-redux";
import { CheckPermissions } from "../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";

import store from "../../../../../../store";
import { setPage } from "../../../../../../features/websiteActionSlice";
import { formatDate } from '../../../../../../utils/helpers';
interface Props {
  tableHeadings: { [key: string]: string };
  websiteList: any[];
  webSiteIds: any;
}

function WebsiteActionTable({
  tableHeadings,
  websiteList,

  webSiteIds,
}: Props) {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [additionalInfo, setAdditionalInfo] = useState<any>([]);
  const headCells = Object.entries(tableHeadings);
  const [sort, setSort] = useState<{
    keytoSort: string | undefined;
    direction: "asc" | "desc" | undefined;
  }>({
    keytoSort: "modified",
    direction: "desc",
  });
  const [sortedData, setSortedData] = useState<any[]>([]);
  const {
    selectedType,
    selectedTeritory,
    selectedIndustry,
    selectedStatus,
    syncEnabled,
    selectedWebsite,
    page,
  } = useSelector((state: any) => state.websiteAction);
  const response = useSelector(selectUserData);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  //permissions checks
  const hasEditWebsitePermission = CheckPermissions(
    response?.data?.products?.assets,
    "websites",
    "edit_websites"
  );
  const hasManageWebsiteSettingsPermission = CheckPermissions(
    response?.data?.products?.assets,
    "websites",
    "manage_website_settings"
  );

  const tableheaders = [
    { id: 0, KEY: "ui_site_name", label: "Site Name", sortable: true },
    { id: 1, KEY: "site_type", label: "Type", sortable: true },
    {
      id: 2,
      KEY: "territories__territory_name",
      label: "Territory",
      sortable: true,
    },
    {
      id: 3,
      KEY: "industries__industry_name",
      label: "Industry",
      sortable: true,
    },
    { id: 4, KEY: "is_active", label: "Status", sortable: true },
    { id: 5, KEY: "sync_enabled", label: "Sync", sortable: true },
    { id: 6, KEY: "timezone", label: "Time Zone", sortable: true },
    { id: 7, KEY: "last_edited", label: "Last Edited", sortable: true },
    { id: 8, KEY: "last_edited_by", label: "Edited By", sortable: true },
    { id: 9, KEY: "quick_actions", label: "Quick Actions", sortable: false },
    { id: 10, KEY: "more_info", label: "More info", sortable: false },
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    setSort({
      keytoSort: header.KEY,
      // Toggle sort direction if the same column is clicked, otherwise default to ascending
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  useEffect(() => {
    const sortedRows = getSortedData(websiteList || []);
    setSortedData(sortedRows);
  }, [websiteList, getSortedData]);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    store.dispatch(setPage(pageFromUrl || 0)); // Ensure it defaults correctly
    //eslint-disable-next-line
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    store.dispatch(setPage(newPage));
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    store.dispatch(setPage(0));
  };

  const [openRow, setOpenRow] = React.useState<number | null>(null);

  const handleRowClick = (index: number, row: any) => {
    setAdditionalInfo(row);
    setOpenRow((prev) => (prev === index ? null : index));
  };

  const filteredData = sortedData.filter((website: any) => {
    const selectedWebsiteName =
      selectedWebsite && selectedWebsite.length !== 0
        ? selectedWebsite.includes(website?.ui_site_name)
        : true; // If no Asset is selected, treat it as a match

    const selectedTypeName =
      selectedType && selectedType.length !== 0
        ? selectedType.includes(website?.site_type)
        : true; // If no Asset is selected, treat it as a match

    const selectedTeritoryName =
      selectedTeritory && selectedTeritory.length !== 0
        ? selectedTeritory.includes(website?.territories__territory_name)
        : true; // If no Asset is selected, treat it as a match

    const selectedIndustryName =
      selectedIndustry && selectedIndustry.length !== 0
        ? selectedIndustry.includes(website?.industries__industry_name)
        : true; // If no Asset is selected, treat it as a match

    const selectedStatusName =
      selectedStatus && selectedStatus.length !== 0
        ? selectedStatus.includes(String(website?.is_active))
        : true; // If no Asset is selected, treat it as a match

    const selectedSyncName =
      syncEnabled && syncEnabled.length !== 0
        ? syncEnabled.includes(String(website?.sync_enabled))
        : true;

    return (
      selectedTypeName &&
      selectedTeritoryName &&
      selectedIndustryName &&
      selectedStatusName &&
      selectedWebsiteName &&
      selectedSyncName
    );
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (page !== 0) {
      store.dispatch(setPage(0));
      if (params.has("page")) {
        store.dispatch(setPage(Number(params.get("page")))); // Ensure it's a number, default to 1
      }
    }
    //eslint-disable-next-line
  }, [
    selectedType,
    selectedTeritory,
    selectedIndustry,
    selectedStatus,
    selectedWebsite,
    syncEnabled,
  ]);

  const handleNavigateToWebsiteSetting = (row: any) => {
    const params = new URLSearchParams(location.search);
    params.set("websiteSharedUid", row);

    navigate(`website_setting?${params.toString()}`);
  };

  const handleNavigateToEditWebsite = (row: any) => {
    const params = new URLSearchParams(location.search);
    params.set("websiteSharedUid", row?.shared_uid);

    navigate(`edit_website?${params.toString()}`);
  };

  const renderTableCellData = (key:any, row:any, index:any) => {
    switch (key) {
      case "more_info":
        return (
          <IconButton
            data-tracker="45"
            aria-label="expand row"
            size="small"
            color="secondary"
            onClick={() => handleRowClick(index, row)}
          >
            {openRow === index ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon data-tracker="45" />
            )}
          </IconButton>
        );
  
      case "ui_site_name":
        return (
          <Link
            data-tracker="40"
            href={row?.site_url}
            target="_blank"
            color="secondary"
            sx={{
              "&:hover": {
                color: "secondary.main",
                textDecoration: "underline",
              },
            }}
          >
            {row[key] || ""}
          </Link>
        );
  
      case "is_active":{
        const text_isActive = row.is_active === 1 ? "Online" : "Offline";
        const bgColor = row.is_active === 1 ? "#EDF7ED" : "#FDEDED";
        const borderColor = row.is_active === 1
          ? "1px solid #2E7D3280"
          : "1px solid #D32F2F80";
        const textColor = row.is_active === 1 ? "#1E4620" : "#5F2120";
        const Icon = row.is_active === 1
          ? CheckCircleOutlinedIcon
          : CancelOutlinedIcon;
  
        return (
          <Alert
            variant="outlined"
            severity="success"
            icon={<Icon sx={{ color: textColor }} fontSize="small" />}
            sx={{
              display: "flex",
              alignItems: "center",
              marginBlock: "8px",
              padding: "0px 8px 0px 4px",
              background: bgColor,
              width: "100%",
              height: "26px",
              border: borderColor,
              color: textColor,
              "& .MuiAlert-icon": {
                marginRight: 1,
              },
            }}
          >
            {text_isActive}
          </Alert>
        );
      }

      case "sync_enabled": {
        const text = row.sync_enabled === 1 ? "Active" : "Inactive";
        const bgColor = row.sync_enabled === 1 ? "#EDF7ED" : "#FDEDED";
        const borderColor = row.sync_enabled === 1
          ? "1px solid #2E7D3280"
          : "1px solid #D32F2F80";
        const textColor = row.sync_enabled === 1 ? "#1E4620" : "#5F2120";
        const Icon = row.sync_enabled === 1
          ? CheckCircleOutlinedIcon
          : CancelOutlinedIcon;
  
        return (
          <Alert
            variant="outlined"
            severity="success"
            icon={<Icon sx={{ color: textColor }} fontSize="small" />}
            sx={{
              display: "flex",
              alignItems: "center",
              marginBlock: "8px",
              padding: "0px 8px 0px 4px",
              background: bgColor,
              width: "100%",
              height: "26px",
              border: borderColor,
              color: textColor,
              "& .MuiAlert-icon": {
                marginRight: 1,
              },
            }}
          >
            {text}
          </Alert>
        );
      }
  
      case "last_edited_by":
        return (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar
              alt="user"
              src={row.imageUrl || ""}
              sx={{ width: 24, height: 24 }}
            >
              {row[key]?.[0] || "?"}
            </Avatar>
            <Typography fontSize={14}>{row[key] || ""}</Typography>
          </Stack>
        );
        
      case "last_edited":
        return (
          <Typography fontSize={14}>
            {row[key] ? formatDate(row[key]) : ""}
          </Typography>
        );
  
      case "territory":
        return row[key] && <Typography fontSize={14}>{row[key]}</Typography>;
  
      case "quick_actions":
        return (
          <Box display="flex" gap={1} paddingLeft={1}>
            {hasEditWebsitePermission ? (
              <Tooltip title="Edit" placement="top">
                <IconButton
                  data-tracker="41"
                  size="small"
                  color="secondary"
                  sx={{ whiteSpace: "nowrap" }}
                  onClick={() => handleNavigateToEditWebsite(row)}
                >
                  <CreateOutlinedIcon fontSize="small" color="secondary" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Edit" placement="top">
                <IconButton size="small" disabled color="secondary">
                  <CreateOutlinedIcon fontSize="small" color="disabled" />
                </IconButton>
              </Tooltip>
            )}
  
            {hasManageWebsiteSettingsPermission &&
            webSiteIds?.includes(row?.shared_uid) ? (
              <Tooltip title="Setting" placement="top">
                <IconButton
                  data-tracker="43"
                  size="small"
                  color="secondary"
                  onClick={() => handleNavigateToWebsiteSetting(row?.shared_uid)}
                >
                  <TuneOutlinedIcon fontSize="small" color="secondary" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Setting" placement="top">
                <IconButton size="small" disabled color="secondary">
                  <TuneOutlinedIcon fontSize="small" color="disabled" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
  
      case "pages_in_group":
        return (
          <Link href="#" color="secondary">
            {row[key] || ""}
          </Link>
        );
  
      default:
        return row[key] || "";
    }
  };
  

  return (
    <TableContainer
      className="quaterlygoalswrap"
      component={Paper}
      sx={{ marginTop: "16px" }}
    >
      <Table aria-label="collapsible table" size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {tableheaders.map((header, index) => (
              <TableCell key={index} align="left">
                <TableSortLabel
                  active={sort.keytoSort === header.KEY}
                  direction={
                    sort.keytoSort === header.KEY ? sort.direction : "asc"
                  }
                  onClick={() => handleHeaderClick(header)}
                  disabled={!header.sortable}
                >
                  {header.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredData.length > 0 ? (
            filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <React.Fragment key={row.cron_id}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                   {headCells.map(([key], idx) => (
                      <TableCell key={idx} align="left"  data-tracker="45">
                        {renderTableCellData(key, row, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      style={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        border: "none",
                      }}
                      colSpan={Object.keys(tableHeadings).length + 1}
                    >
                      <Collapse
                        in={openRow === index}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ padding: "20px 0px" }}>
                          <WebsiteAdditionalInfo
                            websiteList={websiteList}
                            additionalInfo={additionalInfo}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
          ) : (
            <TableRow>
              <TableCell colSpan={headCells.length} align="center">
                <Typography variant="body1" color="textSecondary">
                  No data to show
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
              {
                display: "none",
              },
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-toolbar": {
              paddingLeft: "16px",
            },
          }}
        />
        <Pagination
          count={Math.ceil(filteredData.length / rowsPerPage)}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          sx={{
            ".css-wjh20t-MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
          onChange={(_, value) => handleChangePage(null, value - 1)}
          page={page + 1}
        />
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".css-levciy-MuiTablePagination-displayedRows": {
              display: "none",
            },
            ".MuiTablePagination-input": {
              marginRight: "15px",
            },
          }}
        />
      </Stack>
    </TableContainer>
  );
}

export default WebsiteActionTable;
